import type { UserContext } from './user-context';
import type { DashboardSessionSummary } from './use-user-context';
import { analiticaActivaEnNavegador } from '@/lib/analytics/entorno';

type MaybePostHog = {
  __loaded?: boolean;
  get_session_replay_url?: (opts?: {
    withTimestamp?: boolean;
    timestampLookBack?: number;
  }) => string;
};

/**
 * Pull the live PostHog replay URL. Tries `window.posthog` first (posthog-js
 * self-attaches there on init), then falls back to the lazy-loaded singleton
 * the app already uses (`lib/analytics/posthog-client.ts`). If neither is
 * live yet — the user escalated before posthog had a chance to initialise —
 * returns `null` and the ticket just goes through without the field.
 *
 * Kept sync because the sink calls `getMetadata()` synchronously at submit
 * time. By the point a ticket is being filed the user has interacted
 * plenty, so the deferred init in `instrumentation-client.ts` has almost
 * certainly already fired.
 *
 * `quiet` skips the debug line (for callers that poll, see `waitForReplayUrl`).
 */
export function currentReplayUrl({ quiet = false }: { quiet?: boolean } = {}): string | null {
  if (typeof window === 'undefined') return null;

  // Primary source: window.posthog, set by posthog-js on init().
  let ph: MaybePostHog | undefined = (window as unknown as { posthog?: MaybePostHog })
    .posthog;

  // Fallback: the lazy singleton. `_posthog` lives inside posthog-client.ts,
  // but require()-ing that module here would bundle posthog eagerly. Instead
  // we peek at globalThis for a convention we expose below.
  if (!ph?.__loaded) {
    ph = (globalThis as unknown as { __ezdocu_posthog__?: MaybePostHog })
      .__ezdocu_posthog__;
  }

  if (!ph?.__loaded || typeof ph.get_session_replay_url !== 'function') {
    if (!quiet && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug('[extractTicketMetadata] posthog not loaded yet — replay URL skipped', {
        hasWindowPosthog: !!(window as { posthog?: unknown }).posthog,
        hasFallback: !!(globalThis as { __ezdocu_posthog__?: unknown }).__ezdocu_posthog__,
      });
    }
    return null;
  }

  try {
    const url = ph.get_session_replay_url({ withTimestamp: true, timestampLookBack: 30 });
    return typeof url === 'string' && url.length > 0 ? url : null;
  } catch {
    return null;
  }
}

/**
 * Same URL as `currentReplayUrl`, for callers that can afford a short wait
 * (the credit-claim submit). PostHog is lazy-loaded and only runs in
 * production: there, if it is not live yet, poll for up to `maxWaitMs`; outside
 * production it never loads, so return null right away. Never throws — a
 * missing URL just means the ticket goes out without it.
 */
export async function waitForReplayUrl({
  maxWaitMs = 1500,
  intervalMs = 100,
  isActive = analiticaActivaEnNavegador,
}: { maxWaitMs?: number; intervalMs?: number; isActive?: () => boolean } = {}): Promise<string | null> {
  try {
    const now = currentReplayUrl({ quiet: true });
    if (now || !isActive()) return now;
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, Math.max(0, Math.min(intervalMs, deadline - Date.now()))));
      const url = currentReplayUrl({ quiet: true });
      if (url) return url;
    }
    return currentReplayUrl();
  } catch {
    return null;
  }
}

const ORDER_ROUTE_RE =
  /\/(dashboard|admin|workspace)\/orders\/([0-9a-f-]{8,})(?:\/|$|\?)/i;

/**
 * Best-effort mapping from the current user context to the metadata we want
 * attached to any ticket created from the widget at this moment. We only
 * capture things that make agent-side triage faster:
 *   - orderId when the user is sitting on an order page
 *   - route so admins can see where the user was
 *   - userRole / accountPlan so admins can filter "trial-plan complaints"
 * Everything is optional. Returning `null` means "no useful metadata".
 */
export function extractTicketMetadata(
  ctx: UserContext | null | undefined,
  session?: DashboardSessionSummary | null,
): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};

  if (ctx?.route) {
    out.route = ctx.route;
    const match = ctx.route.match(ORDER_ROUTE_RE);
    if (match) out.orderId = match[2];
  }

  const docFromParams = ctx?.routeParams?.documentId ?? ctx?.routeParams?.docId;
  if (typeof docFromParams === 'string' && docFromParams.length >= 8) {
    out.documentId = docFromParams;
  }

  if (ctx?.userRole) out.userRole = ctx.userRole;
  if (ctx?.accountPlan) out.accountPlan = ctx.accountPlan;

  if (session?.userId) out.userId = session.userId;
  if (session?.userEmail) out.userEmail = session.userEmail;
  if (session?.userName) out.userName = session.userName;
  if (session?.accountId) out.accountId = session.accountId;
  if (session?.accountName) out.accountName = session.accountName;

  const replayUrl = currentReplayUrl();
  if (replayUrl) out.sessionReplayUrl = replayUrl;

  return Object.keys(out).length > 0 ? out : null;
}
